export const PACK_RANKS = ['jack', 'queen', 'king', 'ace', 'joker'] as const;
export const PACK_SUITS = ['hearts', 'diamonds', 'spades', 'clubs'] as const;
export const PACK_COLOURS = ['red', 'black'] as const;
export type PackRank = (typeof PACK_RANKS)[number];
export type PackSuit = (typeof PACK_SUITS)[number];
export type PackColour = (typeof PACK_COLOURS)[number];

/** Jack, Queen and King must be covered for every suit; Ace and Joker are optional extras. */
export const REQUIRED_RANKS = ['jack', 'queen', 'king'] as const;

export const SUIT_COLOUR: Record<PackSuit, PackColour> = { hearts: 'red', diamonds: 'red', spades: 'black', clubs: 'black' };

/**
 * A pack's images are named `<rank>-<colour>.webp` (red covers hearts and diamonds, black covers spades and clubs; the
 * Joker exists only in these two) and may be refined per suit with `<rank>-<suit>.webp`, which wins over the colour file for
 * that suit. A creator needs six images (Jack, Queen, King x red, black) and may add up to twenty more.
 */
export const COLOUR_FILE_NAMES: readonly string[] = PACK_RANKS.flatMap((r) => PACK_COLOURS.map((c) => `${r}-${c}.webp`));
export const SUIT_FILE_NAMES: readonly string[] = PACK_RANKS.filter((r) => r !== 'joker').flatMap((r) => PACK_SUITS.map((s) => `${r}-${s}.webp`));
/** Every file name a pack may contain. */
export const ALLOWED_FILE_NAMES: readonly string[] = [...COLOUR_FILE_NAMES, ...SUIT_FILE_NAMES];

/**
 * Which file draws this card, or null if the pack has none (the app then shows its classic art for that card).
 * A suit-specific file wins over the colour file. The Joker has no suit: pass its colour.
 */
export function resolvePackFile(has: (name: string) => boolean, rank: PackRank, suitOrColour: PackSuit | PackColour): string | null {
  const colour = (PACK_COLOURS as readonly string[]).includes(suitOrColour) ? (suitOrColour as PackColour) : SUIT_COLOUR[suitOrColour as PackSuit];
  if (rank !== 'joker' && (PACK_SUITS as readonly string[]).includes(suitOrColour)) {
    const specific = `${rank}-${suitOrColour}.webp`;
    if (has(specific)) return specific;
  }
  const general = `${rank}-${colour}.webp`;
  return has(general) ? general : null;
}

/** Size limits. The first-party packs are 768x1280 and 60-230 KB a file. */
export const LIMITS = {
  maxFileBytes: 800 * 1024,
  maxPackBytes: 10 * 1024 * 1024,
  minWidth: 384,
  minHeight: 640,
  maxWidth: 1536,
  maxHeight: 2560,
  /** width / height must be 3:5 within this relative tolerance. */
  aspectTolerance: 0.02,
} as const;

export const LICENCES = ['proprietary', 'CC0', 'CC-BY', 'CC-BY-SA', 'CC-BY-NC'] as const;
export type Licence = (typeof LICENCES)[number];

/**
 * `everyone` packs may be shown in the kids edition (once approved and shared by a parent); `mature` packs
 * (undead, war, anything scary) are adult-edition only.
 */
export const CONTENT_RATINGS = ['everyone', 'mature'] as const;
export type ContentRating = (typeof CONTENT_RATINGS)[number];
